// ============================================================
// TILT — PHYSICS WORLD  (the deterministic solver)
// ------------------------------------------------------------
// A fixed-timestep pinball solver. Circles (balls) against static
// and kinematic geometry (segments, arcs, bumpers, flippers).
//
// ── THE FOUR LAWS OF THIS SOLVER ─────────────────────────────
//
//   LAW 1 — FIXED STEP, ALWAYS.
//     step() advances exactly FIXED_DT seconds. The render loop
//     accumulates real elapsed time and calls step() 0..N times.
//     Physics NEVER sees a variable dt. This is what makes the
//     sim reproducible: same seed + same inputs = same ball.
//     A 144Hz monitor and a 60Hz monitor get the SAME game.
//
//   LAW 2 — SUB-STEP THE FAST BALL (poor man's CCD).
//     A pinball at full tilt moves further per frame than the
//     thickness of a wall. Naive discrete collision = tunneling
//     (the "ball fell through the table" bug that kills every
//     hobby pinball project). We split each step into N sub-steps
//     sized so the ball never travels more than SUBSTEP_MAX_TRAVEL
//     table-units at a time. Cheap, robust, no swept-shape math.
//
//   LAW 3 — SPEED CEILING.
//     Even with sub-stepping, an unbounded velocity (a Magnetize
//     stacked on an Overclock into a bumper chain) can outrun the
//     solver. MAX_SPEED is a hard clamp. It is not a tuning knob;
//     it is a safety rail.
//
//   LAW 4 — NO Math.random IN THE SOLVER.
//     Every scatter/jitter pulls from the world's seeded RNG
//     (see Vec.makeRng). Determinism or nothing.
//
// ── COLLIDER TYPES ───────────────────────────────────────────
//   seg     — line segment with thickness. Walls, ramps, guides,
//             slingshot faces, and (kinematically) flipper bodies.
//   circle  — a static disc. Posts, pegs, bumper cores.
//   arc     — a circular wall span (the top arch, orbit lanes).
//             Ball collides with the INSIDE or OUTSIDE face.
//
// Everything on a TILT table is one of those three. A bumper is a
// circle with a big kick. A flipper is a seg whose endpoints move.
// A drain is a sensor. That's the whole ontology.
//
// ── TAGS & EVENTS ────────────────────────────────────────────
// Every collider carries a free-form `tag` and optional `id`. The
// solver does NOT know what a bumper "means" — it just reports
// `{ type:"hit", id, tag, ballIdx, speed, point }` into an event
// queue. GAME LOGIC LIVES ABOVE THE SOLVER, ALWAYS. That
// separation is why the same solver runs the puzzle tables, the
// score tables, and the PvP tables with zero forks.
// ============================================================

import {
  add, sub, mul, dot, len, norm, perp, reflect, clamp,
  closestOnSeg, dist, makeRng,
} from "./Vec";

// ── Solver constants (table units, seconds) ───────────────────
export const FIXED_DT = 1 / 120;         // 120Hz physics — smooth flippers
const SUBSTEP_MAX_TRAVEL = 3.0;          // ball moves ≤3u per sub-step
const MAX_SUBSTEPS = 8;                  // ceiling on sub-step count
export const MAX_SPEED = 900;            // hard velocity clamp (u/s)
const SLEEP_SPEED = 0.4;                 // below this, ball is "resting"
const CONTACT_SLOP = 0.01;               // penetration tolerance

// ── Ball ──────────────────────────────────────────────────────
export const makeBall = (x, y, opts = {}) => ({
  p:  { x, y },
  vel: { x: opts.vx || 0, y: opts.vy || 0 },
  r:  opts.r ?? 7,
  mass: opts.mass ?? 1,
  alive: true,
  // Per-ball modifiers — powers write these, the solver reads them.
  frozen: 0,        // seconds of Freeze remaining (velocity held)
  ghost: 0,         // seconds of Ghost remaining (passes through `ghostable`)
  speedMul: 1,      // Overclock
  gravityMul: 1,    // gravity-zone / power override
  id: opts.id ?? 0,
  owner: opts.owner ?? "p1",
  // Sensor latch — which sensors this ball is currently INSIDE.
  // Sensors are edge-triggered (fire on entry, once), and the latch
  // lives on the BALL rather than the collider so that two balls can
  // occupy the same gate independently. See World._sensorPass().
  inSensor: {},
  // Trail for rendering (ring buffer of recent positions)
  trail: [],
});

// ── World ─────────────────────────────────────────────────────
export class World {
  constructor(opts = {}) {
    this.gravity   = opts.gravity   ?? 420;   // u/s² — table tilt, not earth
    this.damping   = opts.damping   ?? 0.9992; // per-substep air drag
    this.seed      = opts.seed      ?? 1;
    this.rng       = makeRng(this.seed);

    this.balls     = [];
    this.colliders = [];   // static + kinematic geometry
    this.fields    = [];   // gravity zones, magnet fields (see applyFields)
    this.events    = [];   // drained each frame by the game layer
    this.time      = 0;    // accumulated sim time (deterministic clock)
    this.frame     = 0;    // sub-step counter — the deterministic tick id
  }

  // ── Authoring ───────────────────────────────────────────────
  addBall(b)      { this.balls.push(b); return b; }
  addCollider(c)  { this.colliders.push(c); return c; }
  addField(f)     { this.fields.push(f); return f; }

  removeCollider(id) {
    this.colliders = this.colliders.filter((c) => c.id !== id);
  }
  getCollider(id) {
    return this.colliders.find((c) => c.id === id) || null;
  }

  emit(e) { this.events.push(e); }
  drainEvents() { const e = this.events; this.events = []; return e; }

  // ── The step ────────────────────────────────────────────────
  // Advances exactly FIXED_DT. Internally sub-steps as needed so a
  // fast ball can't tunnel. Kinematic colliders (flippers) are
  // advanced by the caller BEFORE step() — the solver reads their
  // current a/b and their `pointVel(p)` to impart momentum.
  step() {
    const live = this.balls.filter((b) => b.alive);
    if (live.length === 0) { this.time += FIXED_DT; return; }

    // How far will the fastest ball travel this step?
    let maxTravel = 0;
    for (const b of live) {
      const s = len(b.vel) * (b.frozen > 0 ? 0 : b.speedMul) * FIXED_DT;
      if (s > maxTravel) maxTravel = s;
    }
    const n = clamp(
      Math.ceil(maxTravel / SUBSTEP_MAX_TRAVEL) || 1,
      1, MAX_SUBSTEPS
    );
    const h = FIXED_DT / n;

    for (let i = 0; i < n; i++) this._substep(h, live);

    this.time += FIXED_DT;
  }

  _substep(h, live) {
    this.frame++;

    for (const b of live) {
      if (!b.alive) continue;

      // Timers tick even while frozen — Freeze must expire.
      if (b.frozen > 0) {
        b.frozen = Math.max(0, b.frozen - h);
        // A frozen ball holds position AND velocity. It is a
        // held breath. When it thaws, it resumes exactly where
        // it was going — that's what makes Freeze a REPOSITION
        // window and not a free redirect.
        continue;
      }
      if (b.ghost > 0) b.ghost = Math.max(0, b.ghost - h);

      // ── Forces ──────────────────────────────────────────────
      b.vel.y += this.gravity * b.gravityMul * h;
      this._applyFields(b, h);

      // Air drag
      b.vel.x *= this.damping;
      b.vel.y *= this.damping;

      // Speed clamp (LAW 3)
      const sp = len(b.vel);
      if (sp > MAX_SPEED) {
        const k = MAX_SPEED / sp;
        b.vel.x *= k; b.vel.y *= k;
      }

      // ── Integrate ───────────────────────────────────────────
      const mul_ = b.speedMul;
      b.p.x += b.vel.x * mul_ * h;
      b.p.y += b.vel.y * mul_ * h;

      // ── Resolve ─────────────────────────────────────────────
      this._collide(b);

      // Trail (render-only; capped, never read by the solver)
      if (this.frame % 3 === 0) {
        b.trail.push({ x: b.p.x, y: b.p.y });
        if (b.trail.length > 14) b.trail.shift();
      }
    }
  }

  // ── Fields — gravity zones, magnets, wind ───────────────────
  // A field is a region + an acceleration function. Powers push
  // temporary fields here (Magnetize) and tables author permanent
  // ones (a zero-g chamber, a sideways-gravity tunnel).
  _applyFields(b, h) {
    for (const f of this.fields) {
      if (f.ttl !== undefined && f.ttl <= 0) continue;
      if (!f.contains(b.p)) continue;
      const a = f.accel(b.p, b.vel);
      b.vel.x += a.x * h;
      b.vel.y += a.y * h;
      if (f.gravityMul !== undefined) b.gravityMul = f.gravityMul;
    }
  }

  // Decrement field lifetimes. Called once per FRAME by the game
  // layer (not per sub-step) so a 2s magnet lasts 2 real seconds.
  tickFields(dt) {
    for (const f of this.fields) {
      if (f.ttl !== undefined) f.ttl -= dt;
    }
    this.fields = this.fields.filter((f) => f.ttl === undefined || f.ttl > 0);
  }

  // ── Collision resolution ────────────────────────────────────
  _collide(b) {
    this._sensorPass(b);
    this._solidPass(b);
  }

  // ── SENSORS — report, never deflect. Drains, rollovers, lane
  //    triggers, scoring gates, puzzle-state checks.
  //
  // ── EDGE-TRIGGERED, NOT LEVEL-TRIGGERED ─────────────────────
  // A sensor fires ONCE, on ENTRY, then latches until the ball
  // leaves it.
  //
  // The naive version emits on every step the ball OVERLAPS the
  // sensor. That sounds harmless and is catastrophic: at 120Hz a
  // ball crossing a 20-unit gate fires it ~20 times, so one trip
  // through a ramp scores 20× what the table says it scores. On
  // Spite that compounded through the combo multiplier into SIX
  // MILLION points from a single launch, with the player never
  // touching a flipper.
  //
  // Worse, the inflation factor is the ball's DWELL TIME — a SLOW
  // ball scored MORE than a fast one, which is exactly backwards,
  // and it meant every number in every table's scoreTable was
  // silently meaningless. Balancing on top of that would have been
  // balancing on sand.
  //
  // This pass is SEPARATE from the solid pass, and deliberately
  // does not `break`. The solid pass breaks on first contact (see
  // below) — if sensors shared that loop, a sensor sitting after
  // the first solid hit would never be seen, its latch would look
  // "exited," and it would re-fire on the very next step. Which is
  // the bug we just removed, wearing a hat.
  //
  // The latch lives on the BALL (`b.inSensor`), not the collider,
  // because two balls (hello, Split) must be able to occupy the
  // same gate independently.
  _sensorPass(b) {
    const touched = b._touched || (b._touched = new Set());
    touched.clear();

    for (const c of this.colliders) {
      if (!c.sensor || c.disabled) continue;
      if (b.ghost > 0 && c.ghostable) continue;

      let hit = null;
      if      (c.kind === "seg")    hit = this._hitSeg(b, c);
      else if (c.kind === "circle") hit = this._hitCircle(b, c);
      else if (c.kind === "arc")    hit = this._hitArc(b, c);
      if (!hit) continue;

      touched.add(c.id);
      if (!b.inSensor[c.id]) {
        b.inSensor[c.id] = true;
        this.emit({
          type: "sensor", id: c.id, tag: c.tag,
          ball: b, ballId: b.id, owner: b.owner,
          point: hit.point, speed: len(b.vel),
        });
      }
    }

    // Release the latch on anything the ball has LEFT. This is what
    // re-arms a sensor for the next trip through it.
    for (const id in b.inSensor) {
      if (!touched.has(id)) delete b.inSensor[id];
    }
  }

  _solidPass(b) {
    for (const c of this.colliders) {
      if (c.sensor || c.disabled) continue;
      if (b.ghost > 0 && c.ghostable) continue;

      let hit = null;
      if      (c.kind === "seg")    hit = this._hitSeg(b, c);
      else if (c.kind === "circle") hit = this._hitCircle(b, c);
      else if (c.kind === "arc")    hit = this._hitArc(b, c);

      if (!hit) continue;

      // ── SOLID — push out, reflect, impart kinematic momentum.
      const n = hit.normal;               // unit, pointing AT the ball
      const pen = hit.pen;

      // De-penetrate (never leave the ball inside geometry — that's
      // how a ball gets "stuck vibrating" in a wall forever).
      if (pen > CONTACT_SLOP) {
        b.p.x += n.x * (pen + CONTACT_SLOP);
        b.p.y += n.y * (pen + CONTACT_SLOP);
      }

      // Surface velocity — a moving flipper HITS the ball. Without
      // this, a flipper is just a wall the ball rests on. WITH it,
      // a flipper flings. This one term is the entire game feel.
      const surfV = c.pointVel ? c.pointVel(hit.point) : { x: 0, y: 0 };
      const rel   = sub(b.vel, surfV);

      const approaching = dot(rel, n) < 0;
      if (!approaching) continue;   // already separating — no double-hit

      const e   = c.restitution ?? 0.55;
      const out = reflect(rel, n, e);

      // Tangential friction — bleeds spin-less sliding along walls.
      const t  = perp(n);
      const vt = dot(out, t);
      const fr = c.friction ?? 0.02;
      const outT = vt * (1 - fr);

      const vn = dot(out, n);
      b.vel = add(
        { x: n.x * vn + t.x * outT, y: n.y * vn + t.y * outT },
        surfV
      );

      // Active kick — bumpers, slingshots, kickers. A flat impulse
      // ALONG THE NORMAL, on top of the bounce. This is what makes a
      // bumper feel alive instead of feeling like a rock.
      if (c.kick) {
        const jitter = c.kickJitter ? (this.rng() - 0.5) * c.kickJitter : 0;
        const kn = norm({ x: n.x + jitter * -n.y, y: n.y + jitter * n.x });
        b.vel.x += kn.x * c.kick;
        b.vel.y += kn.y * c.kick;
      }

      const speed = len(b.vel);
      this.emit({
        type: "hit", id: c.id, tag: c.tag,
        ball: b, ballId: b.id, owner: b.owner,
        point: hit.point, normal: n, speed,
      });

      // One collider per sub-step per ball is enough — sub-stepping
      // is fine-grained enough that the next iteration catches any
      // second contact, and this prevents corner-jitter (a ball in
      // a 90° corner resolving twice and squirting out at Mach 3).
      break;
    }
  }

  // ── Segment (thick line) ────────────────────────────────────
  _hitSeg(b, c) {
    const a = c.a, e = c.b;
    const cp = closestOnSeg(b.p, a, e);
    const d  = sub(b.p, cp);
    const dl = len(d);
    const R  = b.r + (c.thickness ?? 0) * 0.5;
    if (dl > R) return null;

    let n;
    if (dl < 1e-6) {
      // Ball center exactly on the line — use the segment normal.
      n = norm(perp(norm(sub(e, a))));
    } else {
      n = { x: d.x / dl, y: d.y / dl };
    }
    // One-sided walls (ramp floors, one-way gates): only collide
    // from the face side.
    if (c.oneSided && c.face && dot(n, c.face) < 0) return null;

    return { normal: n, pen: R - dl, point: cp };
  }

  // ── Circle (post, peg, bumper core) ─────────────────────────
  _hitCircle(b, c) {
    const d  = sub(b.p, c.c);
    const dl = len(d);
    const R  = b.r + c.r;
    if (dl > R) return null;
    const n = dl < 1e-6 ? { x: 0, y: -1 } : { x: d.x / dl, y: d.y / dl };
    return {
      normal: n,
      pen: R - dl,
      point: { x: c.c.x + n.x * c.r, y: c.c.y + n.y * c.r },
    };
  }

  // ── Arc (curved wall span) ──────────────────────────────────
  // `inner:true` = ball is INSIDE the arc, collides with the inner
  // face (an orbit lane, the top arch). `inner:false` = ball outside,
  // collides with the outer face (a curved bumper hull).
  _hitArc(b, c) {
    const d  = sub(b.p, c.c);
    const dl = len(d);
    if (dl < 1e-6) return null;

    // Angular span check
    let ang = Math.atan2(d.y, d.x);
    if (!_inArc(ang, c.a0, c.a1)) return null;

    const half = (c.thickness ?? 2) * 0.5;
    const n0 = { x: d.x / dl, y: d.y / dl };

    if (c.inner) {
      // Wall is at radius c.r; ball must stay INSIDE (dl < c.r).
      const surf = c.r - half;
      if (dl + b.r < surf) return null;

      // ══════════════════════════════════════════════════════════
      //  ⚠ THE UNBOUNDED INNER ARC  —  A REAL SOLVER BUG
      // ══════════════════════════════════════════════════════════
      //
      // The line above rejects a ball that is too CLOSE to the
      // centre. NOTHING rejected a ball that was too FAR.
      //
      // Read the geometry: an `inner` arc's contract is "the ball
      // lives inside this ring." The test `dl + b.r >= surf` asks
      // "has the ball reached the wall?" — and a ball THREE HUNDRED
      // UNITS AWAY, on the other side of the table, satisfies that
      // trivially. It is very much "beyond" the inner surface.
      //
      // So the solver would compute a contact, find an enormous
      // penetration depth, and de-penetrate the ball by yanking it
      // bodily across the playfield and slapping it onto the rim.
      //
      // ── HOW IT HID FOR THIS LONG ────────────────────────────
      // Because until now, the ONLY inner arc in the entire codebase
      // was `arch` — the ceiling, whose radius wraps the whole
      // playfield. Every ball is genuinely inside it, always, so the
      // missing bound never mattered. The bug was latent, and it was
      // waiting for the first person to put a SMALL ring somewhere
      // in open play.
      //
      // That was the whirlpool. The instant it went in, every
      // launched ball teleported from the plunger lane (385,660) to
      // the pool rim (147,397) IN ONE STEP, at full speed, and was
      // swallowed. 60 launches out of 60. And it read, in the sim,
      // exactly like a table-design problem — "the pool is eating
      // the launch, move the pool" — so it survived TWO ROUNDS of
      // joint placement sweeps, 320 candidate layouts, every single
      // one of which reported the same 24/24 failure. Of course they
      // did. The position was never the variable.
      //
      // A layout sweep cannot find a physics bug. It will happily
      // search a space of three hundred wrong answers and report,
      // truthfully, that none of them work.
      //
      // ── THE FIX ─────────────────────────────────────────────
      // An arc is a WALL, not a force field. It has a thickness, and
      // contact means TOUCHING it. A ball whose surface is further
      // than the wall's own half-thickness from the wall's radius is
      // not touching it, whichever side it's on.
      if (dl - b.r > c.r + half) return null;

      return {
        normal: { x: -n0.x, y: -n0.y },       // push toward center
        pen: (dl + b.r) - surf,
        point: { x: c.c.x + n0.x * surf, y: c.c.y + n0.y * surf },
      };
    }
    // Outer face — ball stays OUTSIDE (dl > c.r).
    const surf = c.r + half;
    if (dl - b.r > surf) return null;
    return {
      normal: n0,
      pen: surf - (dl - b.r),
      point: { x: c.c.x + n0.x * surf, y: c.c.y + n0.y * surf },
    };
  }
}

// Angle-in-arc test that survives wraparound.
function _inArc(ang, a0, a1) {
  const TAU = Math.PI * 2;
  let a = ((ang - a0) % TAU + TAU) % TAU;
  let span = ((a1 - a0) % TAU + TAU) % TAU;
  if (span < 1e-6) span = TAU;   // full circle
  return a <= span;
}

// ── Collider factories ────────────────────────────────────────
// Sugar so table files read like a level, not like a physics dump.

let _cid = 0;
const nid = (id) => (id !== undefined ? id : `c${++_cid}`);

export const Seg = (ax, ay, bx, by, o = {}) => ({
  kind: "seg", id: nid(o.id), tag: o.tag ?? "wall",
  a: { x: ax, y: ay }, b: { x: bx, y: by },
  thickness: o.thickness ?? 4,
  restitution: o.restitution ?? 0.45,
  friction: o.friction ?? 0.03,
  kick: o.kick ?? 0,
  kickJitter: o.kickJitter ?? 0,
  sensor: !!o.sensor,
  ghostable: !!o.ghostable,
  oneSided: !!o.oneSided,
  face: o.face ?? null,
  disabled: !!o.disabled,

  // ── BUILD-TIME ASSERTION FLAGS ──────────────────────────────
  // These do NOTHING at runtime. The solver never reads them. They
  // exist purely so TableKit's structural laws can be told "yes, I
  // know, this one is deliberate."
  //
  // ⚠ THEY MUST BE COPIED THROUGH HERE OR THEY VANISH.
  //   Seg() is an ALLOWLIST — it builds a fresh object from named
  //   fields and silently drops everything else. `ledgeOk: true`
  //   was passed in, dropped on the floor, and the ledge law kept
  //   throwing on a wall that had already been exempted. There was
  //   no error. The property simply ceased to exist between the
  //   call and the object.
  //
  //   That is EXACTLY the Firebase `$extra:false` failure mode,
  //   reimplemented in the physics layer by accident: a field you
  //   set, that silently isn't there. If you add a new collider
  //   flag anywhere, ADD IT HERE TOO, or you will spend an hour
  //   debugging a value you can see in your own source.
  ledgeOk: !!o.ledgeOk,   // "this flat wall is a ceiling / unreachable"
  wedgeOk: !!o.wedgeOk,   // "this tight gap is unreachable"

  // ── CHAIN — "I am one link of a longer wall" ────────────────
  // Build-time only; the solver never reads it. Two colliders with
  // the SAME chain name are never wedge-checked against each other,
  // because they are not two walls — they are two links of ONE
  // curved wall, and the seam between them is a JOINT, not a GAP.
  //
  // ⚠ AND YES, IT HAS TO BE LISTED HERE. This is the FOURTH flag to
  //   be passed into Seg() and silently dropped on the floor by this
  //   allowlist (after ledgeOk, wedgeOk and dwell). The property
  //   simply ceases to exist between the call and the object, with
  //   no error and nothing to grep for, and you lose an hour to a
  //   value you can read in your own source.
  //
  //   If you add a collider flag ANYWHERE, add it here. Every time.
  chain: o.chain,

  // Seconds the ball must STAY inside this sensor for it to fire.
  // 0 = fires on entry (the default, and every sensor before now).
  dwell: o.dwell ?? 0,

  ...o.extra,
});

export const Circle = (cx, cy, r, o = {}) => ({
  kind: "circle", id: nid(o.id), tag: o.tag ?? "post",
  c: { x: cx, y: cy }, r,
  restitution: o.restitution ?? 0.6,
  friction: o.friction ?? 0.01,
  kick: o.kick ?? 0,
  kickJitter: o.kickJitter ?? 0,
  sensor: !!o.sensor,
  ghostable: !!o.ghostable,
  disabled: !!o.disabled,

  // Build-time assertion flags (see Seg — same allowlist trap).
  ledgeOk: !!o.ledgeOk,
  wedgeOk: !!o.wedgeOk,
  chain:   o.chain,

  // ── DWELL ───────────────────────────────────────────────────
  // Seconds the ball must remain INSIDE this sensor for it to count.
  // 0 (default) = fires on entry, like every sensor always has.
  //
  // This is how a lock asks "did the ball STAY?" instead of "did the
  // ball TOUCH?" — two completely different questions that a plain
  // edge-triggered sensor cannot tell apart. Lockstep's Magnetize
  // lock is the whole reason it exists: its premise is that gravity
  // never lets a ball rest in the alcove, so you must HOLD it there —
  // but the sensor fired on entry, so any doomed ball drifting
  // through solved the lock for free, 5% of the time.
  //
  // No amount of geometry fixes that, because the geometry was never
  // wrong. The VERB was.
  dwell: o.dwell ?? 0,

  ...o.extra,
});

// Bumper: a circle that KICKS. The archetype.
export const Bumper = (cx, cy, r, o = {}) =>
  Circle(cx, cy, r, {
    tag: "bumper",
    restitution: 0.35,          // low bounce...
    kick: o.kick ?? 260,        // ...but a big flat kick. That's the pop.
    kickJitter: o.kickJitter ?? 0.18,
    ...o,
  });

export const Arc = (cx, cy, r, a0, a1, o = {}) => ({
  kind: "arc", id: nid(o.id), tag: o.tag ?? "wall",
  c: { x: cx, y: cy }, r, a0, a1,
  inner: o.inner !== false,     // default: it's a lane the ball rides inside
  thickness: o.thickness ?? 4,
  restitution: o.restitution ?? 0.4,
  friction: o.friction ?? 0.02,
  kick: o.kick ?? 0,
  kickJitter: 0,
  sensor: !!o.sensor,
  ghostable: !!o.ghostable,
  disabled: !!o.disabled,
  ledgeOk: !!o.ledgeOk,
  wedgeOk: !!o.wedgeOk,
  chain:   o.chain,
  dwell: o.dwell ?? 0,
  ...o.extra,
});

// Sensor zone — a fat invisible segment. Drains, rollovers, gates.
export const SensorSeg = (ax, ay, bx, by, o = {}) =>
  Seg(ax, ay, bx, by, { sensor: true, thickness: o.thickness ?? 10, ...o });

export const SensorCircle = (cx, cy, r, o = {}) =>
  Circle(cx, cy, r, { sensor: true, ...o });

// ── Fields ────────────────────────────────────────────────────
// Magnetize: pull the ball toward a point, inverse-square-ish but
// clamped so it never becomes a singularity that eats the solver.
export const MagnetField = (cx, cy, o = {}) => {
  const c = { x: cx, y: cy };
  const R = o.radius ?? 140;
  const strength = o.strength ?? 900;
  return {
    id: o.id ?? `mag${++_cid}`,
    kind: "magnet",
    c, radius: R, ttl: o.ttl,
    contains: (p) => {
      const dx = p.x - c.x, dy = p.y - c.y;
      return dx * dx + dy * dy < R * R;
    },
    accel: (p) => {
      const dx = c.x - p.x, dy = c.y - p.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const falloff = clamp(1 - d / R, 0, 1);
      const s = strength * falloff * falloff;
      return { x: (dx / d) * s, y: (dy / d) * s };
    },
  };
};

// Gravity zone — an authored region where down isn't down.
// `mul: 0` = zero-g chamber. `dir` = a sideways/upward pull.
export const GravityZone = (x, y, w, h, o = {}) => ({
  id: o.id ?? `gz${++_cid}`,
  kind: "gravity",
  ttl: o.ttl,
  gravityMul: o.mul ?? 0,
  contains: (p) => p.x >= x && p.x <= x + w && p.y >= y && p.y <= y + h,
  accel: () => o.dir ?? { x: 0, y: 0 },
  rect: { x, y, w, h },
});

// ── Flipper (kinematic) ───────────────────────────────────────
// A flipper is a SEGMENT whose endpoints move, plus the one term
// that makes pinball feel like pinball: pointVel(). When the
// flipper is swinging, every point on it has a velocity, and the
// solver adds that to the bounce. A stationary flipper is a wall.
// A swinging one is a bat.
export class Flipper {
  constructor(o) {
    this.id     = o.id;
    this.pivot  = { x: o.x, y: o.y };
    this.length = o.length ?? 62;
    this.side   = o.side ?? "L";           // "L" | "R"
    this.restAng   = o.restAng   ?? (o.side === "R" ? Math.PI - 0.50 : 0.50);
    this.activeAng = o.activeAng ?? (o.side === "R" ? Math.PI + 0.52 : -0.52);
    this.speed  = o.speed ?? 26;            // rad/s — flip snap
    this.returnSpeed = o.returnSpeed ?? 14; // slower return (real machines do this)
    this.ang    = this.restAng;
    this.angVel = 0;
    this.pressed = false;
    this.radius = o.radius ?? 6;            // thickness of the bat

    // Its collider — the solver reads a/b every step; we rewrite them.
    this.collider = {
      kind: "seg", id: o.id, tag: "flipper",
      a: { x: 0, y: 0 }, b: { x: 0, y: 0 },
      thickness: this.radius * 2,
      restitution: o.restitution ?? 0.30,   // the BAT doesn't bounce much...
      friction: 0.06,
      kick: 0,
      kickJitter: 0,
      sensor: false,
      ghostable: false,
      disabled: false,
      // ...the SWING is what launches the ball. Momentum, not spring.
      pointVel: (p) => {
        const r = sub(p, this.pivot);
        // v = ω × r  (2D: perpendicular scaled by angular velocity)
        return { x: -r.y * this.angVel, y: r.x * this.angVel };
      },
      // Tip cap so a ball can't slip past the end of the bat.
      _flipper: this,
    };
    // Pivot post — the ball must not pass THROUGH the hinge.
    this.post = Circle(o.x, o.y, this.radius + 1, {
      id: `${o.id}_post`, tag: "flipper_post", restitution: 0.3,
    });
    this._sync();
  }

  _sync() {
    const c = Math.cos(this.ang), s = Math.sin(this.ang);
    this.collider.a = { x: this.pivot.x, y: this.pivot.y };
    this.collider.b = {
      x: this.pivot.x + c * this.length,
      y: this.pivot.y + s * this.length,
    };
    this.tip = this.collider.b;
  }

  press(down) { this.pressed = !!down; }

  // Advance the flipper. Called BEFORE world.step() every sub-step
  // window, so the solver always sees the flipper where it IS.
  update(dt) {
    const target = this.pressed ? this.activeAng : this.restAng;
    const rate   = this.pressed ? this.speed : this.returnSpeed;
    const diff   = target - this.ang;
    const stepA  = Math.sign(diff) * rate * dt;

    if (Math.abs(stepA) >= Math.abs(diff)) {
      this.angVel = diff / dt;      // final partial step — exact arrival
      this.ang = target;
    } else {
      this.angVel = stepA / dt;
      this.ang += stepA;
    }
    // A flipper at rest has zero angular velocity — it must not
    // impart momentum just by existing.
    if (Math.abs(diff) < 1e-6) this.angVel = 0;
    this._sync();
  }

  mount(world) {
    world.addCollider(this.collider);
    world.addCollider(this.post);
    return this;
  }
}

export { closestOnSeg, dist, len, norm, sub, add, mul, dot, clamp };
